import { collectVisitorId } from './collectVisitorId';
import { mapAuthError } from './mapAuthError';
import { err, ok } from './result';
import type { RegisterError, Result } from './result';
import { z } from 'zod';

export type RegisterWithGoogleParams = {
  welcomeBonus: string;
  affiliateCode: string;
  partner: string | null;
  selectedCountryCode: string;
  signInWithGoogle: () => Promise<void>;
};

export const registerWithGoogle = async ({
  welcomeBonus,
  affiliateCode,
  partner,
  selectedCountryCode,
  signInWithGoogle,
}: RegisterWithGoogleParams): Promise<Result<void, RegisterError>> => {
  const visitorId = await collectVisitorId();
  const preflight = await registrationPreflight({
    welcomeBonus,
    visitorId,
    affiliateCode,
    partner,
  });

  if (!preflight.ok) {
    return err(
      mapAuthError(
        preflight.error.message,
        preflight.error.code,
      ),
    );
  }

  localStorage.setItem(
    'pendingGoogleRegistration',
    JSON.stringify({
      welcomeBonus,
      country: selectedCountryCode,
      affiliatorId: affiliateCode,
      partner: partner ?? '',
      visitorId: visitorId ?? '',
      timestamp: Date.now(),
    }),
  );

  try {
    await signInWithGoogle();
  } catch (error: unknown) {
    localStorage.removeItem('pendingGoogleRegistration');

    const message = error instanceof Error ? error.message.toLowerCase() : '';
    const isCancelled = message.includes('cancelled') || message.includes('popup');

    return err({
      code: isCancelled ? 'google_cancelled' : 'general',
      message: isCancelled ? 'Registro con Google cancelado' : 'Error al iniciar sesión con Google',
    });
  }

  return ok(undefined);
};


export type RegistrationPreflightInput = {
  welcomeBonus: string;
  visitorId: string | null;
  affiliateCode: string;
  partner: string | null;
};

const RegistrationPreflightResponseSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
  }),
  z.object({
    ok: z.literal(false),
    error: z.object({
      code: z.string().optional(),
      message: z.string(),
    }),
  }),
]);

export type RegistrationPreflightResponse = z.infer<
  typeof RegistrationPreflightResponseSchema
>;

export const registrationPreflight = async (
  input: RegistrationPreflightInput,
): Promise<RegistrationPreflightResponse> => {
  try {
    const response = await fetch('/api/auth/registration-preflight', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const json = await response.json();

    const parsed = RegistrationPreflightResponseSchema.safeParse(json);

    if (!parsed.success) {
      return {
        ok: false,
        error: {
          code: 'invalid_response',
          message: 'Invalid server response',
        },
      };
    }

    return parsed.data;
  } catch {
    return {
      ok: false,
      error: {
        code: 'network_error',
        message: 'No se pudo validar el registro',
      },
    };
  }
};